/**
 * 색인(SEO) 대상 경로 SSoT — "이 URL 을 검색엔진에 정식 문서로 내놓는가" 의 단일 판정 지점.
 *
 * 소비처(전부 이 파일만 본다 — 경로 목록을 다른 곳에 다시 쓰지 말 것):
 *  - 프리렌더 대상: `src/ssg/routes.ts` 가 정적 목록 + 동적 패턴을 실데이터로 펼친다.
 *  - canonical `<link>`: `useCanonicalHead` 가 {@link isIndexablePath} 로 발행 여부를 가른다.
 *  - `robots` noindex: `useRouteHead` 가 색인 대상이 아닌 경로에 noindex 를 붙인다(SPA soft-404 차단).
 *  - sitemap·llms.txt: 빌드가 **실제로 프리렌더된 HTML 중 noindex 가 아닌 것**만 싣는다(vite.config.ts) —
 *    별도 목록을 두지 않으므로 이 파일과 어긋날 수 없다.
 *
 * 경로 표기는 슬래시 없는 라우트 경로(`/guide`)다. 실제 정식 URL 은 `canonicalUrl()` 이 trailing slash 로
 * 정규화한다(근거: brand.ts). 판정은 슬래시 유무를 모두 받아들인다(nginx 디렉토리 301 후 도착하는 형태).
 */

/** 데이터 없이 경로만으로 정해지는 색인 대상. */
export const STATIC_INDEXABLE_PATHS: readonly string[] = [
  '/',
  '/materials',
  '/guide',
  '/review',
  '/notice',
  '/news',
  '/legal/terms',
  '/legal/privacy',
  '/legal/business',
  // AI 입시분석 워크스페이스 — 랜딩 + 비로그인 샘플 뷰 3화면(077, 진입 시 세션 없으면 샘플 렌더).
  '/analysis',
  '/analysis/jeongsi',
  '/analysis/susi',
  '/analysis/tests',
];

/**
 * 파라미터로 갈라지는 색인 대상 — 실제 값은 빌드 시 데이터로 펼친다(`src/ssg/routes.ts`).
 * 여기 없는 동적 라우트(마이페이지·리포트 상세 등)는 전부 비색인이다.
 */
export const DYNAMIC_INDEXABLE_PATTERNS: readonly RegExp[] = [
  /^\/guide\/[^/]+$/,
  /^\/products\/[^/]+$/,
  /^\/notice\/\d+$/,
  /^\/news\/\d+$/,
];

/**
 * 한시 이벤트 — **프리렌더만 하고 색인하지 않는다**. 카톡·커뮤니티 공유 미리보기(OG)를 위해 완성 HTML 은
 * 필요하지만, 종료 후에도 검색 결과에 남으면 안 된다(페이지가 자체 noindex 를 낸다 → sitemap 자동 제외).
 * 이벤트 종료 후 이 목록에서만 빼면 된다.
 */
export const EVENT_PRERENDER_PATHS: readonly string[] = ['/event', '/event/interview'];

/** canonical 을 데이터 기준으로 직접 발행하는 라우트 name — `useCanonicalHead` 는 건너뛴다(중복 방지). */
export const SELF_CANONICAL_ROUTE_NAMES: ReadonlySet<string> = new Set(['guide', 'guide-index']);

/** trailing slash 를 걷어낸 라우트 경로(루트는 그대로 `/`). */
function stripTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/** 이 경로가 검색 색인 대상인가 — 쿼리·해시는 넣지 않는다(route.path 기준). */
export function isIndexablePath(path: string): boolean {
  const p = stripTrailingSlash(path);
  return STATIC_INDEXABLE_PATHS.includes(p) || DYNAMIC_INDEXABLE_PATTERNS.some((re) => re.test(p));
}
